/**
 * Iter 111 / 116 — Maintenance service detail editor.
 *
 * Iter 116 rewrite — operator-friendly back-calculator:
 *
 *   ┌─ Job total $ + Materials $ + Hours ─┐
 *   │  Backend math: net = total − materials
 *   │  Labour $/hr = 0.80 × net / hours      ← derived
 *   │  Contractor $/hr = 0.20 × net / hours  ← derived
 *   └─────────────────────────────────────────┘
 *
 * Save target:
 *   • Override for this appraisal only     → writes detail_override.rate
 *   • Apply to size tier (Medium · Iron)   → PATCH /size-tiers/{svc}/{tier}
 *
 * `service`     — the entry from /maintenance-builder/defaults
 * `override`    — saved detail_override block on this appraisal's spec
 * `tier`        — currently-selected tier on the service row (s/m/l)
 * `surface`     — currently-selected surface (Iron / Modern Conc / …)
 * `tierRow`     — the matching maintenance_size_tiers row (if loaded);
 *                  used to seed the form with the current persisted
 *                  values.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Calculator, X, Save, Loader2, AlertCircle, Info, RotateCcw } from 'lucide-react';

const TIER_LABEL = { small: 'Small', medium: 'Medium', large: 'Large' };

export default function MaintenanceServiceDetailModal({
  api, open, onOpenChange, service, override, tier, surface, tierRow,
  onSaveOverride, onMasterSaved, onResetOverride,
}) {
  const [target, setTarget] = useState('override');
  const [draft, setDraft]   = useState({});
  const [busy, setBusy]     = useState(false);
  const [err, setErr]       = useState(null);

  // Seed the draft each time the modal opens. Per-appraisal override
  // values win (operator is editing those); fall back to the live
  // tier row (so the persisted master rates appear); finally fall
  // back to the service defaults.
  useEffect(() => {
    if (!open || !service) return;
    const ov = override || {};
    const tr = tierRow || {};
    const seedTotal = (
      ov.rate ??
      tr.flat_rate ??
      service.rate ??
      service.default_rate ?? 0
    );
    const seedMaterials = (
      ov.materials_cost ??
      tr.materials_cost ??
      Math.round(seedTotal * 0.30 * 100) / 100
    );
    const seedBuy = (
      ov.buy_cost ??
      tr.buy_cost ??
      Math.round(seedMaterials * 0.65 * 100) / 100  // default: ~65% of sales-side materials
    );
    const seedHours = (
      ov.labour_hours ??
      tr.labour_hours ??
      { small: 1.0, medium: 2.0, large: 3.5 }[tier] ?? 2.0
    );
    setDraft({
      total_rate:     Number(seedTotal),
      materials_cost: Number(seedMaterials),
      buy_cost:       Number(seedBuy),
      labour_hours:   Number(seedHours),
      supplier:       ov.supplier        ?? tr.supplier        ?? '',
      supplier_code:  ov.supplier_code   ?? tr.supplier_code   ?? '',
      materials_desc: ov.materials_desc  ?? tr.materials_desc  ?? '',
      notes:          ov.description     ?? '',
    });
    setErr(null);
    setTarget('override');
  }, [open, service, override, tier, surface, tierRow]);

  // Live back-calc — labour $/hr + contractor $/hr split 80/20 of the
  // net (total − materials) divided across the operator's hours. We
  // surface both rates inline so the office can verify the labour-cost
  // calc before pushing the tier change live.
  const calc = useMemo(() => {
    const total = Number(draft.total_rate)     || 0;
    const mat   = Number(draft.materials_cost) || 0;
    const buy   = Number(draft.buy_cost)       || 0;
    const hrs   = Number(draft.labour_hours)   || 0;
    const net   = Math.max(0, total - mat);
    const lpr   = hrs > 0 ? Math.round((net * 0.80 / hrs) * 100) / 100 : 0;
    const cpr   = hrs > 0 ? Math.round((net * 0.20 / hrs) * 100) / 100 : 0;
    // Iter 123 — Buy → margin% on materials line (sales-side cost
    // tracking). Helps the operator price-check supplier increases.
    const matMarginPct = mat > 0 ? Math.round(((mat - buy) / mat) * 100) : null;
    return { total, mat, buy, hrs, net, labour_per_hr: lpr, contractor_per_hr: cpr, mat_margin_pct: matMarginPct };
  }, [draft.total_rate, draft.materials_cost, draft.buy_cost, draft.labour_hours]);

  const patchField = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  const tierTargetEnabled = !!(tier && service?.key);
  const tierLabel = `${TIER_LABEL[tier] || ''}${surface ? ' · ' + surface.replace(/_/g, ' ') : ''}`;

  const handleSave = async () => {
    setBusy(true);
    setErr(null);
    try {
      if (target === 'master') {
        if (!tierTargetEnabled) {
          throw new Error('Pick a size tier on the service row first before applying to the master tier rate.');
        }
        // Hit the new tier-aware endpoint so the change applies to
        // every appraisal using this service + surface + tier.
        await api.patch(
          `/maintenance-builder/size-tiers/${service.key}/${tier}`,
          {
            surface,
            flat_rate:           calc.total,
            materials_cost:      calc.mat,
            buy_cost:            calc.buy,
            labour_hours:        calc.hrs,
            labour_rate_per_hr:     calc.labour_per_hr,
            contractor_rate_per_hr: calc.contractor_per_hr,
          },
        );
        if (onMasterSaved) await onMasterSaved();
      } else {
        // Per-appraisal override — stamp the total flat rate +
        // back-calc metadata onto the spec's detail_override block.
        // The backend treats detail_override.rate as the line total
        // when a tier is active.
        const body = {
          rate:           calc.total,
          materials_rate: calc.mat,
          buy_cost:       calc.buy,
          labour_rate:    Math.round(calc.net * 0.80 * 100) / 100,
          contractor_rate: Math.round(calc.net * 0.20 * 100) / 100,
          labour_hours_per_unit: calc.hrs,
          description:    draft.notes,
          supplier:       draft.supplier,
          supplier_code:  draft.supplier_code,
          materials_desc: draft.materials_desc,
        };
        if (onSaveOverride) await onSaveOverride(body);
      }
      onOpenChange(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  if (!open || !service) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto"
         onClick={() => onOpenChange(false)}
         data-testid="maintenance-service-detail-modal">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl my-8" onClick={(e) => e.stopPropagation()}>
        <Header service={service} tierLabel={tierLabel} onClose={() => onOpenChange(false)} />
        <div className="p-5 space-y-4">
          <BackCalcPanel draft={draft} calc={calc} unit={service.unit} onChange={patchField} />
          <SupplyPanel draft={draft} onChange={patchField} />
          <SaveTargetPanel target={target} setTarget={setTarget}
                           tierLabel={tierLabel} tierEnabled={tierTargetEnabled} />
          {err && (
            <div data-testid="maintenance-service-detail-error" className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2 flex items-center gap-1.5">
              <AlertCircle size={12} /> {err}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
          {/* Iter 128 — Reset-to-tier button. Visible only when a
              per-appraisal override is currently active. One click
              clears the override and the service reverts to the
              shared size-tier rate. */}
          {override && Object.keys(override).length > 0 && tierTargetEnabled && (
            <button type="button" onClick={async () => {
                      if (!onResetOverride) return;
                      setBusy(true); setErr(null);
                      try {
                        await onResetOverride();
                        onOpenChange(false);
                      } catch (e) {
                        setErr(e?.response?.data?.detail || e.message || 'Reset failed');
                      } finally {
                        setBusy(false);
                      }
                    }}
                    disabled={busy}
                    data-testid="maintenance-service-detail-reset-to-tier"
                    title={`Clear this appraisal's custom rate and revert to the ${tierLabel} size-tier rate.`}
                    className="px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-amber-100 hover:text-amber-900 rounded border border-amber-300 bg-amber-50 flex items-center gap-1 mr-auto">
              <RotateCcw size={11} /> Reset to tier rate
            </button>
          )}
          <button type="button" onClick={() => onOpenChange(false)} disabled={busy}
                  data-testid="maintenance-service-detail-cancel"
                  className="px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-200 rounded">
            Cancel
          </button>
          <button type="button" onClick={handleSave} disabled={busy || (target === 'master' && !tierTargetEnabled)}
                  data-testid="maintenance-service-detail-save"
                  className={`px-4 py-1.5 text-xs font-bold text-white rounded disabled:opacity-50 flex items-center gap-1.5 shadow-sm ${
                    target === 'master' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-[#D35400] hover:bg-[#A04000]'
                  }`}>
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
            {busy ? 'Saving…' : (target === 'master'
              ? `Apply to size tier${tierLabel ? ' · ' + tierLabel : ''}`
              : 'Save override')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Panels ────────────────────────────────────────────────────── */

function Header({ service, tierLabel, onClose }) {
  return (
    <div className="px-5 py-3 border-b border-slate-200 bg-gradient-to-r from-emerald-50 to-teal-50 flex items-start justify-between gap-3">
      <div>
        <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">
          Edit maintenance rate · {service.key}
        </div>
        <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2 mt-0.5">
          <Calculator size={18} className="text-emerald-700" /> {service.label}
          {tierLabel && (
            <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-cyan-100 text-cyan-800 border border-cyan-300">
              {tierLabel}
            </span>
          )}
        </h3>
        <div className="text-[11px] text-slate-500 mt-0.5">
          Enter the full job total, materials cost and hours — the form will back-derive Labour $/hr + Contractor $/hr.
        </div>
      </div>
      <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700"
              data-testid="maintenance-service-detail-close">
        <X size={18} />
      </button>
    </div>
  );
}

function BackCalcPanel({ draft, calc, unit, onChange }) {
  return (
    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
      <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 mb-3 flex items-center gap-1">
        <Calculator size={11} /> Back-calculator
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <CalcField label={`Job total $ (full ${unit || ''} fee)`} value={draft.total_rate}
                   onChange={(v) => onChange('total_rate', v)} step="0.01" prefix="$"
                   testid="maintenance-service-detail-total_rate" />
        <CalcField label="Materials $ (sales)" value={draft.materials_cost}
                   onChange={(v) => onChange('materials_cost', v)} step="0.01" prefix="$"
                   testid="maintenance-service-detail-materials_cost" />
        <CalcField label="Buy cost $ (supplier)" value={draft.buy_cost}
                   onChange={(v) => onChange('buy_cost', v)} step="0.01" prefix="$"
                   testid="maintenance-service-detail-buy_cost" />
        <CalcField label="Labour hours" value={draft.labour_hours}
                   onChange={(v) => onChange('labour_hours', v)} step="0.25" suffix="hr"
                   testid="maintenance-service-detail-labour_hours" />
      </div>
      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        <Stat label="Net (after materials)" value={`$${calc.net.toFixed(2)}`} testid="maintenance-service-detail-net" />
        <Stat label="Material margin"
              value={calc.mat_margin_pct == null ? '—' : `${calc.mat_margin_pct}%`}
              testid="maintenance-service-detail-mat-margin" />
        <Stat label="Labour $/hr · 80% of net"
              value={calc.labour_per_hr > 0 ? `$${calc.labour_per_hr.toFixed(2)}/hr` : '—'}
              testid="maintenance-service-detail-labour-per-hr" />
        <Stat label="Contractor $/hr · 20% of net"
              value={calc.contractor_per_hr > 0 ? `$${calc.contractor_per_hr.toFixed(2)}/hr` : '—'}
              testid="maintenance-service-detail-contractor-per-hr" />
      </div>
      <div className="mt-2 text-[10px] text-slate-500 flex items-start gap-1">
        <Info size={10} className="mt-0.5 flex-shrink-0" />
        <span>
          Buy = supplier cost (internal), Materials = sales-side rate. Material margin shows the
          markup. Labour/Contractor $/hr derive from Net (Job total − Materials) using the office's
          standard 80/20 split. Save target below decides whether this applies to just this
          appraisal or to every job at this size tier.
        </span>
      </div>
    </div>
  );
}

function SupplyPanel({ draft, onChange }) {
  const [open, setOpen] = useState(false);
  const hasAny = !!(draft.supplier || draft.supplier_code || draft.materials_desc);
  return (
    <div className="bg-slate-50 border border-slate-200 rounded">
      <button type="button"
              onClick={() => setOpen(o => !o)}
              data-testid="maintenance-service-detail-supply-toggle"
              className="w-full flex items-center justify-between px-3 py-2 text-left">
        <span className="text-[10px] uppercase tracking-wider font-bold text-slate-600">
          Materials supply &amp; notes {hasAny && <em className="text-teal-700 normal-case">· filled</em>}
        </span>
        <span className="text-slate-400 text-xs">{open ? '−' : '+'}</span>
      </button>
      {open && (
        <div className="px-3 pb-3 grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
          <TextField label="Materials description" value={draft.materials_desc}
                     onChange={(v) => onChange('materials_desc', v)}
                     placeholder="e.g. Wet & Forget Hyper 5L"
                     testid="maintenance-service-detail-materials_desc" />
          <TextField label="Product code" value={draft.supplier_code}
                     onChange={(v) => onChange('supplier_code', v)}
                     placeholder="WF-HYPER-5L"
                     testid="maintenance-service-detail-supplier_code" mono />
          <TextField label="Supplier" value={draft.supplier}
                     onChange={(v) => onChange('supplier', v)}
                     placeholder="Wet & Forget NZ"
                     testid="maintenance-service-detail-supplier" />
          <div className="md:col-span-3">
            <TextField label="Notes / job description" value={draft.notes}
                       onChange={(v) => onChange('notes', v)}
                       placeholder="Access route, special conditions…"
                       testid="maintenance-service-detail-notes" />
          </div>
        </div>
      )}
    </div>
  );
}

function SaveTargetPanel({ target, setTarget, tierLabel, tierEnabled }) {
  return (
    <div className="border border-slate-200 rounded p-3">
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">Save target</div>
      <label className="flex items-start gap-2 text-xs cursor-pointer py-1">
        <input type="radio" name="maintenance-service-target" value="override"
               checked={target === 'override'} onChange={() => setTarget('override')}
               data-testid="maintenance-service-detail-target-override"
               className="mt-0.5 accent-[#D35400]" />
        <div>
          <div className="font-semibold text-slate-800">Override for this appraisal only</div>
          <div className="text-slate-500">Persists onto this maintenance spec only — every other appraisal still uses the master tier rate.</div>
        </div>
      </label>
      <label className={`flex items-start gap-2 text-xs cursor-pointer py-1 ${!tierEnabled ? 'opacity-50' : ''}`}>
        <input type="radio" name="maintenance-service-target" value="master"
               checked={target === 'master'} onChange={() => setTarget('master')}
               disabled={!tierEnabled}
               data-testid="maintenance-service-detail-target-master"
               className="mt-0.5 accent-emerald-700" />
        <div>
          <div className="font-semibold text-slate-800">
            Apply to size tier{tierLabel && <span className="text-emerald-700"> · {tierLabel}</span>}
          </div>
          <div className="text-slate-500">
            {tierEnabled
              ? 'Updates the master tier rate row. Every appraisal using this service at this size tier picks up the change on next reload.'
              : 'Pick a size tier on the service row first to enable the tier rate update path.'}
          </div>
        </div>
      </label>
    </div>
  );
}

/* ── Atoms ─────────────────────────────────────────────────────── */

function CalcField({ label, value, onChange, step, prefix, suffix, testid }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase font-bold tracking-wide text-slate-500">{label}</span>
      <div className="relative mt-0.5">
        {prefix && (
          <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 pointer-events-none">{prefix}</span>
        )}
        <input type="number" min="0" step={step} value={value ?? 0}
               onChange={(e) => onChange(e.target.value === '' ? 0 : parseFloat(e.target.value))}
               data-testid={testid}
               className={`w-full ${prefix ? 'pl-4' : 'pl-2'} ${suffix ? 'pr-8' : 'pr-2'} py-1.5 text-sm border border-slate-300 rounded text-right font-mono-num focus:outline-none focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400`} />
        {suffix && (
          <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] text-slate-400 pointer-events-none">{suffix}</span>
        )}
      </div>
    </label>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="bg-white border border-emerald-200 rounded px-2 py-1.5 text-center">
      <div className="text-[9px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
      <div className="font-mono-num font-bold text-emerald-800 text-sm" data-testid={testid}>{value}</div>
    </div>
  );
}

function TextField({ label, value, onChange, placeholder, testid, mono }) {
  return (
    <label className="flex flex-col gap-0.5">
      <span className="text-[10px] text-slate-500 font-semibold">{label}</span>
      <input type="text" value={value || ''}
             onChange={(e) => onChange(e.target.value)}
             placeholder={placeholder}
             data-testid={testid}
             className={`px-2 py-1 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-400 ${mono ? 'font-mono' : ''}`} />
    </label>
  );
}
